import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import FluxoRule from '../components/home/FluxoRule';
import Reveal from '../components/home/Reveal';
import { META_SITE } from '../content/institutionalCopy';
import {
  HOME_COMO,
  HOME_CTA,
  HOME_EXPERIENCIA,
  HOME_HERO,
  HOME_INTRO,
  HOME_PRODUTOS,
  HOME_PROVA,
} from '../content/homeCopy';
import { LINKS_EXTERNOS, formatBRL, getItemDestaquePorSlug, imagemProduto } from '../data/catalogoDestaque';
import { useHashScroll } from '../hooks/useHashScroll';
import { trackPublicEvent } from '../lib/publicAnalytics';

const PRODUTOS = HOME_PRODUTOS.items.flatMap((sel) => {
  const item = getItemDestaquePorSlug(sel.id);
  return item ? [{ item, beneficio: sel.beneficio as string }] : [];
});

export default function Home() {
  useHashScroll();

  useEffect(() => {
    document.title = META_SITE.title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', META_SITE.description);
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1">
        <section className="relative overflow-hidden bg-lunins-cream">
          <img
            src="/images/home/onda-ouro.png?v=3"
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[center_78%] opacity-55"
          />
          <div className="lunins-hero-scrim pointer-events-none absolute inset-0" aria-hidden />

          <div className="relative mx-auto flex min-h-[calc(100svh-4.75rem)] max-w-2xl flex-col justify-center px-6 py-28 text-center md:px-8 md:py-36">
            <h1 className="font-serif text-4xl font-medium leading-[1.08] tracking-tight text-lunins-ink sm:text-5xl md:text-6xl lg:text-[4.2rem]">
              {HOME_HERO.title}
            </h1>
            <p className="mx-auto mt-7 max-w-md text-base leading-relaxed text-lunins-ink/80 md:text-lg">
              {HOME_HERO.lead}
            </p>
            <div className="mt-10">
              <Link
                to="/loja"
                onClick={() => trackPublicEvent('cta_click', { origem: 'home_hero', alvo: '/loja' })}
                className="lunins-btn-primary"
              >
                {HOME_HERO.cta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <section id={HOME_INTRO.id} className="scroll-mt-24 bg-lunins-cream px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-lunins-gold">{HOME_INTRO.eyebrow}</p>
              <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-tight text-lunins-ink md:text-5xl">
                {HOME_INTRO.title}
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-lunins-muted md:text-lg">{HOME_INTRO.body}</p>
              <FluxoRule className="mt-10" />
            </Reveal>
            <div className="mt-14 grid gap-12 border-t border-lunins-border/80 pt-12 md:grid-cols-3 md:gap-16">
              {HOME_INTRO.pontos.map((p, i) => (
                <Reveal key={p.n} delayMs={i * 80}>
                  <p className="font-serif text-2xl text-lunins-gold/80">{p.n}</p>
                  <h3 className="mt-3 font-display text-lg font-semibold text-lunins-ink">{p.t}</h3>
                  <p className="mt-2 max-w-xs text-sm leading-relaxed text-lunins-muted">{p.d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section id={HOME_PRODUTOS.id} className="scroll-mt-24 bg-lunins-cream px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-lunins-gold">{HOME_PRODUTOS.eyebrow}</p>
              <h2 className="mt-4 max-w-xl font-serif text-3xl font-medium leading-tight text-lunins-ink md:text-5xl">
                {HOME_PRODUTOS.title}
              </h2>
            </Reveal>

            <div className="mt-16 space-y-16 md:mt-20 md:space-y-24">
              {PRODUTOS.map(({ item, beneficio }, i) => {
                const invert = i % 2 === 1;
                const src = imagemProduto(item);
                return (
                  <Reveal key={item.id}>
                    <article className="grid items-center gap-8 md:grid-cols-12 md:gap-12">
                      <div className={`md:col-span-7 ${invert ? 'md:order-2' : ''}`}>
                        <Link
                          to={`/loja/${item.id}`}
                          onClick={() =>
                            trackPublicEvent('produto_click', {
                              origem: 'home_destaque',
                              produto_id: item.id,
                              produto_nome: item.nome,
                            })
                          }
                          className="lunins-photo-frame group block"
                        >
                          {src ? (
                            <img
                              src={src}
                              alt={item.nome}
                              className="lunins-product-cutout aspect-[5/4] w-full object-contain p-8 md:p-12"
                              loading="lazy"
                            />
                          ) : null}
                        </Link>
                      </div>
                      <div className={`md:col-span-5 ${invert ? 'md:order-1' : ''}`}>
                        <h3 className="font-serif text-3xl font-medium leading-snug text-lunins-ink md:text-4xl">
                          {beneficio}
                        </h3>
                        <p className="mt-4 text-sm text-lunins-muted">{item.nome}</p>
                        <p className="mt-6 text-sm text-lunins-ink/80">{formatBRL(item.precoAVista)}</p>
                        <p className="mt-1 text-xs text-lunins-muted">
                          ou {item.parcelas}x de {formatBRL(item.parcelaValor)}
                        </p>
                        <Link
                          to={`/loja/${item.id}`}
                          onClick={() =>
                            trackPublicEvent('produto_click', {
                              origem: 'home_destaque_cta',
                              produto_id: item.id,
                              produto_nome: item.nome,
                            })
                          }
                          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-lunins-teal transition hover:text-lunins-ink"
                        >
                          {HOME_PRODUTOS.verItem}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </article>
                  </Reveal>
                );
              })}
            </div>

            <Reveal className="mt-16 text-center md:mt-20">
              <Link
                to="/loja"
                onClick={() => trackPublicEvent('cta_click', { origem: 'home_produtos', alvo: '/loja' })}
                className="lunins-btn-secondary"
              >
                {HOME_PRODUTOS.verTodos}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </section>

        <section id={HOME_EXPERIENCIA.id} className="scroll-mt-24 bg-lunins-cream px-5 py-20 md:px-8 md:py-28">
          <span id="salas" className="sr-only" />
          <span id="processo" className="sr-only" />
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-lunins-gold">{HOME_EXPERIENCIA.eyebrow}</p>
              <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-tight text-lunins-ink md:text-5xl">
                {HOME_EXPERIENCIA.title}
              </h2>
            </Reveal>

            <div className="mt-14 grid gap-6 md:mt-16 md:grid-cols-12 md:gap-8">
              {HOME_EXPERIENCIA.fotos.map((foto, i) => (
                <Reveal key={foto.src} delayMs={i * 90} className={i === 0 ? 'md:col-span-7' : 'md:col-span-5 md:mt-16'}>
                  <figure>
                    <div className="lunins-photo-frame overflow-hidden">
                      <img src={foto.src} alt={foto.alt} className="aspect-[4/5] w-full object-cover" loading="lazy" />
                    </div>
                    <figcaption className="mt-4 flex items-baseline justify-between gap-4">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lunins-gold">{foto.kicker}</span>
                      <span className="text-sm text-lunins-muted">{foto.caption}</span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
              <Link to="/circuito" className="inline-flex items-center gap-2 text-sm font-semibold text-lunins-teal hover:text-lunins-ink">
                {HOME_EXPERIENCIA.circuito}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={LINKS_EXTERNOS.luninsOrg}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-lunins-muted hover:text-lunins-ink"
              >
                {HOME_EXPERIENCIA.institucional}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Reveal>
          </div>
        </section>

        <section id={HOME_COMO.id} className="scroll-mt-24 bg-lunins-sand/40 px-5 py-20 md:px-8 md:py-28">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-lunins-gold">{HOME_COMO.eyebrow}</p>
              <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-tight text-lunins-ink md:text-5xl">
                {HOME_COMO.title}
              </h2>
            </Reveal>
            <div className="mt-14 grid gap-10 border-t border-lunins-border/80 pt-12 md:grid-cols-3 md:gap-14">
              {HOME_COMO.passos.map((p, i) => (
                <Reveal key={p.n} delayMs={i * 70}>
                  <p className="font-serif text-2xl text-lunins-gold/80">{p.n}</p>
                  <h3 className="mt-3 font-display text-lg font-semibold text-lunins-ink">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-lunins-muted">{p.d}</p>
                </Reveal>
              ))}
            </div>
            <p className="mt-12 max-w-2xl text-xs leading-relaxed text-lunins-muted/80">{HOME_COMO.nota}</p>
          </div>
        </section>

        <section id={HOME_PROVA.id} className="scroll-mt-24 bg-lunins-cream px-5 py-20 md:px-8 md:py-28">
          <span id={HOME_PROVA.tutorialId} className="sr-only" />
          <div className="mx-auto max-w-4xl text-center">
            <Reveal>
              <img src="/images/home/simbolo-ouro.png" alt="" className="mx-auto h-8 w-8 object-contain" />
              <blockquote className="mt-10 font-serif text-3xl font-medium italic leading-snug text-lunins-ink md:text-5xl md:leading-[1.15]">
                “{HOME_PROVA.quote}”
              </blockquote>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-lunins-gold">{HOME_PROVA.attribution}</p>
              <FluxoRule className="mt-10 flex justify-center" />
              <a
                href={LINKS_EXTERNOS.instagramReels}
                target="_blank"
                rel="noreferrer"
                className="mt-10 inline-flex items-center gap-2 text-sm font-medium text-lunins-teal hover:text-lunins-ink"
              >
                {HOME_PROVA.tutorial}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Reveal>
          </div>
        </section>

        <section className="bg-lunins-ink px-5 py-20 text-lunins-cream md:px-8 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-serif text-3xl font-medium md:text-5xl">{HOME_CTA.title}</h2>
            <Link
              to="/loja"
              onClick={() => trackPublicEvent('cta_click', { origem: 'home_cta_final', alvo: '/loja' })}
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-lunins-gold px-8 py-4 text-sm font-semibold text-lunins-ink transition hover:bg-lunins-gold-hover"
            >
              {HOME_CTA.button}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
